<script setup>
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import Avatar from 'next/avatar/Avatar.vue';

const props = defineProps({
  row: {
    type: Object,
    required: true,
  },
  compact: {
    type: Boolean,
    default: false,
  },
  deleted: {
    type: Boolean,
    default: false,
  },
});

const route = useRoute();

const routeName = computed(() => `${props.row.original.type}_reports_show`);

const isLinkDisabled = computed(
  () => !props.row.original.id || props.row.original.id === 0
);

const isAgent = computed(() => props.row.original.type === 'agent');

const agentEmail = computed(() => {
  if (!isAgent.value) return '';
  const email = props.row.original.email;
  return typeof email === 'string' ? email.trim() : '';
});

const linkTitle = computed(() => {
  const name = props.row.original.name || '';
  return agentEmail.value ? `${name} — ${agentEmail.value}` : name;
});

const linkTo = computed(() => ({
  name: routeName.value,
  params: { id: props.row.original.id },
  query: route.query,
}));

const rootClass = computed(() => {
  if (props.compact && isAgent.value) {
    return [
      'flex flex-col items-center gap-0.5 min-w-0 max-w-full w-full',
      isLinkDisabled.value ? '' : 'hover:no-underline',
    ];
  }
  if (props.compact) {
    return [
      'block min-w-0 truncate text-n-slate-12 text-center',
      isLinkDisabled.value ? '' : 'hover:underline',
    ];
  }
  if (isAgent.value) {
    return [
      'flex items-center gap-2 min-w-0',
      isLinkDisabled.value ? '' : 'hover:no-underline',
    ];
  }
  return [
    'block min-w-0 truncate text-n-slate-12',
    isLinkDisabled.value ? '' : 'hover:underline',
  ];
});

const showDeleted = computed(() => props.deleted || props.row.original.deleted);
</script>

<template>
  <component
    :is="isLinkDisabled ? 'span' : 'router-link'"
    v-bind="isLinkDisabled ? {} : { to: linkTo }"
    :title="linkTitle"
    :class="rootClass"
  >
    <Avatar
      v-if="isAgent"
      :src="row.original.thumbnail"
      :name="row.original.name"
      :status="row.original.status"
      :size="compact ? 20 : 28"
      hide-offline-status
      rounded-full
      class="shrink-0"
    />
    <div
      :class="isAgent ? 'flex flex-col min-w-0 max-w-full w-full' : 'contents'"
    >
      <span
        class="text-n-slate-12 truncate w-full"
        :class="compact ? 'text-[10px] leading-tight text-center' : ''"
      >
        {{ row.original.name }}
      </span>
      <span
        v-if="agentEmail"
        dir="ltr"
        class="leading-tight text-n-slate-11 truncate w-full text-center"
        :class="compact ? 'text-[9px]' : 'text-xs'"
        :title="agentEmail"
      >
        {{ agentEmail }}
      </span>
      <span
        v-if="showDeleted"
        class="text-[9px] leading-tight text-n-slate-11 truncate w-full text-center"
      >
        {{ $t('SUMMARY_REPORTS.DELETED') }}
      </span>
    </div>
  </component>
</template>
